import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import UploadInput from "@/components/admin/UploadInput";

export function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/summary").then(r => setS(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Overview</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Dashboard</h1>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Members" v={s?.members} />
        <Stat label="Paid Members" v={s?.paid_members} />
        <Stat label="Revenue (paid)" v={s ? `$${s.revenue?.toFixed?.(2) ?? s.revenue}` : "…"} />
        <Stat label="Subscribers" v={s?.subscribers} />
        <Stat label="Blog Posts" v={s?.posts} />
        <Stat label="Videos" v={s?.videos} />
        <Stat label="Events" v={s?.events} />
        <Stat label="Open Requests" v={s?.open_speaking_requests} />
      </div>
    </div>
  );
}
function Stat({ label, v }) {
  return (
    <div className="editorial-card p-5">
      <div className="brand-eyebrow">{label}</div>
      <div className="font-serif text-3xl text-plum mt-2">{v ?? "…"}</div>
    </div>
  );
}

/* Generic CRUD list */
export function CrudList({ title, endpoint, fields, defaultData, columns, description, extraRowAction }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultData);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/${endpoint}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const onEdit = (it) => { setEditing(it); setForm({ ...defaultData, ...it }); setOpen(true); };
  const onNew = () => { setEditing(null); setForm(defaultData); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, form);
      else await api.post(`/admin/${endpoint}`, form);
      toast.success("Saved");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="brand-eyebrow">{description || title}</div>
          <h1 className="font-serif text-4xl text-charcoal mt-2">{title}</h1>
        </div>
        <button onClick={onNew} className="btn-primary" data-testid={`admin-new-${endpoint}`}><Plus className="w-4 h-4" /> New</button>
      </div>

      <div className="mt-8 editorial-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt">
            <tr>{columns.map(c => <th key={c.key} className="text-left p-3 text-charcoal/70 font-medium">{c.label}</th>)}<th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-gold-light hover:bg-cream-alt/50" data-testid={`admin-row-${endpoint}-${it.id}`}>
                {columns.map(c => <td key={c.key} className="p-3 align-top text-charcoal/85">{c.render ? c.render(it) : String(it[c.key] ?? "")}</td>)}
                <td className="p-3 flex gap-2 justify-end">
                  {extraRowAction && extraRowAction(it, load)}
                  <button onClick={() => onEdit(it)} className="text-plum" data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(it.id)} className="text-burgundy" data-testid={`delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="p-6 text-charcoal/50 text-center">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-cream max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="space-y-3">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="text-sm text-charcoal/70">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={f.rows || 4} value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                      className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2 focus:ring-gold focus:ring-2 focus:outline-none" />
                  ) : f.type === "upload" ? (
                    <UploadInput value={form[f.key]} onChange={(v) => setForm({...form, [f.key]: v})} testId={`upload-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                      className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2">
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "boolean" ? (
                    <label className="flex items-center gap-2 mt-1">
                      <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({...form, [f.key]: e.target.checked})} />
                      <span className="text-sm">{f.hint || "Yes"}</span>
                    </label>
                  ) : f.type === "list" ? (
                    <textarea rows={5} placeholder="One item per line" value={(form[f.key] || []).join("\n")}
                      onChange={(e) => setForm({...form, [f.key]: e.target.value.split("\n").filter(Boolean)})}
                      className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2" />
                  ) : (
                    <input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => {
                      const v = f.type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value;
                      setForm({...form, [f.key]: v});
                    }}
                      className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button disabled={busy} className="btn-primary" data-testid="admin-save">{busy ? "…" : "Save"}</button>
              <button type="button" onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* --- Specific admin pages using CrudList --- */
export const BlogAdmin = () => <CrudList
  title="Blog Posts" endpoint="blog"
  defaultData={{ title: "", excerpt: "", body: "", cover_image: "", category: "General", access_level: "public", published: true, featured: false }}
  columns={[
    { key: "title", label: "Title" },
    { key: "category", label: "Category" },
    { key: "access_level", label: "Access" },
    { key: "published", label: "Live", render: (i) => i.published ? "Yes" : "Draft" },
  ]}
  fields={[
    { key: "title", label: "Title" },
    { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
    { key: "body", label: "Body (HTML allowed)", type: "textarea", rows: 8 },
    { key: "cover_image", label: "Cover image", type: "upload" },
    { key: "category", label: "Category" },
    { key: "access_level", label: "Access level", type: "select", options: ["public", "free", "paid"] },
    { key: "published", label: "Published", type: "boolean" },
    { key: "featured", label: "Featured", type: "boolean" },
  ]}
/>;

export const VideosAdmin = () => <CrudList
  title="Videos" endpoint="videos"
  defaultData={{ title: "", description: "", provider: "youtube", embed_url: "", thumbnail: "", duration_min: 0, category: "General", access_level: "public" }}
  columns={[{ key: "title", label: "Title" }, { key: "provider", label: "Provider" }, { key: "access_level", label: "Access" }]}
  fields={[
    { key: "title", label: "Title" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "provider", label: "Provider", type: "select", options: ["youtube", "vimeo", "zoom", "streamyard"] },
    { key: "embed_url", label: "Embed URL (paste Vimeo/YouTube embed URL)" },
    { key: "thumbnail", label: "Thumbnail", type: "upload" },
    { key: "duration_min", label: "Duration (minutes)", type: "number" },
    { key: "category", label: "Category" },
    { key: "access_level", label: "Access", type: "select", options: ["public", "free", "paid"] },
    { key: "featured", label: "Featured (pin to top of Videos page)", type: "boolean" },
  ]}
/>;

export const SeriesAdmin = () => <CrudList
  title="Five Takeaways Series" endpoint="series"
  defaultData={{ title: "", topic: "", cover_image: "", short_description: "", preview_clip_url: "", full_video_url: "", takeaways: [], access_level: "public", featured: false, published: true }}
  columns={[{ key: "title", label: "Title" }, { key: "topic", label: "Topic" }, { key: "featured", label: "Featured", render: (i) => i.featured ? "★" : "" }]}
  fields={[
    { key: "title", label: "Title" },
    { key: "topic", label: "Topic" },
    { key: "cover_image", label: "Cover image", type: "upload" },
    { key: "short_description", label: "Short description", type: "textarea" },
    { key: "preview_clip_url", label: "Preview clip embed URL" },
    { key: "full_video_url", label: "Full video embed URL" },
    { key: "takeaways", label: "Five takeaways (one per line)", type: "list" },
    { key: "access_level", label: "Access", type: "select", options: ["public", "free", "paid"] },
    { key: "featured", label: "Featured", type: "boolean" },
    { key: "published", label: "Published", type: "boolean" },
  ]}
/>;

export const EventsAdmin = () => <CrudList
  title="Events" endpoint="events"
  defaultData={{ title: "", description: "", cover_image: "", starts_at: "", ends_at: "", location: "Online", livestream_url: "", replay_url: "", access_level: "public", price: 0, capacity: null, published: true, external_id: "" }}
  columns={[{ key: "title", label: "Title" }, { key: "starts_at", label: "Starts" }, { key: "access_level", label: "Access" }, { key: "replay_url", label: "Replay", render: (i) => i.replay_url ? (i.swap_source === "scheduler" ? "✓ auto" : i.swap_source === "webhook" ? "✓ webhook" : "✓") : "" }]}
  fields={[
    { key: "title", label: "Title" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "cover_image", label: "Cover image", type: "upload" },
    { key: "starts_at", label: "Starts (ISO)" },
    { key: "ends_at", label: "Ends (ISO)" },
    { key: "location", label: "Location" },
    { key: "livestream_url", label: "Livestream URL (Zoom/Vimeo/YouTube/StreamYard)" },
    { key: "replay_url", label: "Replay URL" },
    { key: "replay_expires_at", label: "Replay expires (ISO)" },
    { key: "external_id", label: "External ID (Zoom webinar ID / StreamYard broadcast ID — used by auto-swap webhook)" },
    { key: "access_level", label: "Access", type: "select", options: ["public", "free", "paid", "purchase"] },
    { key: "price", label: "Price (USD, only for 'purchase')", type: "number" },
    { key: "published", label: "Published", type: "boolean" },
  ]}
  extraRowAction={(item, refresh) => item.livestream_url && !item.replay_url ? (
    <button
      className="text-plum text-xs underline mr-2"
      data-testid={`swap-replay-${item.id}`}
      onClick={async (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!window.confirm("Copy livestream URL → replay URL and set 30-day expiry?")) return;
        await api.post(`/admin/events/${item.id}/swap-to-replay`, { expires_in_days: 30 });
        toast.success("Swapped to replay");
        refresh();
      }}
    >Swap → replay</button>
  ) : null}
/>;

export const PlansAdmin = () => <CrudList
  title="Membership Plans" endpoint="plans"
  defaultData={{ name: "", tier_key: "community", price_monthly: 0, price_yearly: 0, features: [], highlight: false, published: true }}
  columns={[{ key: "name", label: "Name" }, { key: "tier_key", label: "Key" }, { key: "price_monthly", label: "Monthly" }, { key: "price_yearly", label: "Yearly" }]}
  fields={[
    { key: "name", label: "Name" },
    { key: "tier_key", label: "Tier key", type: "select", options: ["community", "chambers", "inner_circle"] },
    { key: "price_monthly", label: "Monthly ($)", type: "number" },
    { key: "price_yearly", label: "Yearly ($)", type: "number" },
    { key: "features", label: "Features", type: "list" },
    { key: "highlight", label: "Highlight", type: "boolean" },
    { key: "published", label: "Published", type: "boolean" },
  ]}
/>;

export const PressAdmin = () => <CrudList
  title="Press Materials" endpoint="press"
  defaultData={{ title: "", description: "", file_url: "", kind: "photo" }}
  columns={[{ key: "title", label: "Title" }, { key: "kind", label: "Kind" }]}
  fields={[
    { key: "title", label: "Title" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "file_url", label: "File", type: "upload" },
    { key: "kind", label: "Kind", type: "select", options: ["photo", "bio", "one_sheet", "logo"] },
  ]}
/>;

export const ResourcesAdmin = () => <CrudList
  title="Downloadable Resources" endpoint="resources"
  defaultData={{ title: "", description: "", file_url: "", access_level: "free" }}
  columns={[{ key: "title", label: "Title" }, { key: "access_level", label: "Access" }]}
  fields={[
    { key: "title", label: "Title" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "file_url", label: "File (PDF)", type: "upload" },
    { key: "access_level", label: "Access", type: "select", options: ["public", "free", "paid"] },
  ]}
/>;

export const DiscountsAdmin = () => <CrudList
  title="Discount Codes" endpoint="discounts"
  defaultData={{ code: "", percent_off: 0, amount_off: 0, active: true }}
  columns={[{ key: "code", label: "Code" }, { key: "percent_off", label: "% off" }, { key: "amount_off", label: "$ off" }, { key: "active", label: "Active", render: (i) => i.active ? "Yes" : "No" }]}
  fields={[
    { key: "code", label: "Code" },
    { key: "percent_off", label: "% off", type: "number" },
    { key: "amount_off", label: "$ off", type: "number" },
    { key: "active", label: "Active", type: "boolean" },
  ]}
/>;
